import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Grid, TextField, Typography, useTheme, CircularProgress, Box, Button,
  FormControlLabel, Autocomplete, Switch, Chip, alpha,
  Collapse, InputAdornment, Divider, IconButton, Dialog, DialogTitle,
  DialogContent, DialogActions, Checkbox, Link,
} from "@mui/material";
import VpnKeyIcon from "@mui/icons-material/VpnKey";
import { FormikProvider, useFormik } from "formik";
import { useNotifier } from "../../../Core/Notifier";
import {
  useCreateTeamMutation, useGetallEnumSearchQuery, useGetallLocationQuery,
  useGetDepartmentQuery, useGetPositionQuery, useGetUserRoleQuery,
  useEnumFindOrCreateMutation, useCheckStaffExistsMutation,
  useGenerateStaffIdQuery, useGenerateStaffUsernameQuery, useCreateDepartmentMutation, useCreatePositionMutation,
} from "../../../Api/attoDeskApi";
import AddressAutocomplete, { ParsedAddress } from "../../../Components/AddressAutocomplete";
import { uploadImage } from "../../../Components/UploadFile/upload";
import { CameraModal, CropModal, enhanceImage } from "../../../Components/ImageCapture";
import { StepUpSpinner } from "../../../Components/Loader";
import {
  formatPhoneNumberForDisplay, formatPhoneNumber, validateEmail, validatePhoneNumber,
} from "../../../Components/Validation/validationUtils";
import {
  CustomerOption, CustomerOptionString, IEnumType, IStudentLocation, IDepartment, IPosition, ITeamCreate,
} from "../../../Api/Interface/api.interface";
import { useNavigate } from "react-router-dom";
import { toProperCase } from "../../../Components/Validation/toProperCase";
import { Gender, TeachMood } from "../../../Core/Enum/enum";

// Icons
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import PhoneIcon from "@mui/icons-material/Phone";
import HomeOutlinedIcon from "@mui/icons-material/HomeOutlined";
import WorkOutlineIcon from "@mui/icons-material/WorkOutline";
import BadgeOutlinedIcon from "@mui/icons-material/BadgeOutlined";
import NotesOutlinedIcon from "@mui/icons-material/NotesOutlined";
import CameraAltIcon from "@mui/icons-material/CameraAlt";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import SaveOutlinedIcon from "@mui/icons-material/SaveOutlined";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import AddIcon from "@mui/icons-material/Add";
import VisibilityIcon from "@mui/icons-material/Visibility";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import RefreshIcon from "@mui/icons-material/Refresh";

const toAllCaps = (s: string | null | undefined): string => s ? s.toUpperCase() : "";

// ── Password generator ──
// 6-character password using letters and numbers only, guaranteed to
// contain at least one letter and one digit.
const generatePassword = (): string => {
  const letters = "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz";
  const digits = "0123456789";
  const all = letters + digits;
  let pw = [
    letters[Math.floor(Math.random() * letters.length)],
    digits[Math.floor(Math.random() * digits.length)],
  ];
  for (let i = 0; i < 4; i++) pw.push(all[Math.floor(Math.random() * all.length)]);
  return pw.sort(() => Math.random() - 0.5).join("");
};

// ── Section Card ──
const SectionCard: React.FC<{
  icon: React.ReactNode; title: string; subtitle?: string; children: React.ReactNode;
  accentColor?: string; action?: React.ReactNode; collapsible?: boolean; defaultOpen?: boolean;
}> = ({ icon, title, subtitle, children, accentColor, action, collapsible, defaultOpen = true }) => {
  const theme = useTheme();
  const color = accentColor || theme.palette.primary.main;
  const [open, setOpen] = useState(defaultOpen);
  return (
    <Box sx={{ bgcolor: "#fff", borderRadius: "12px", border: "1px solid rgba(0,0,0,0.08)", overflow: "hidden", boxShadow: "0 1px 3px rgba(0,0,0,0.04)" }}>
      <Box
        onClick={collapsible ? () => setOpen(!open) : undefined}
        sx={{
          display: "flex", alignItems: "center", gap: 1.5, px: 3, py: 2,
          borderBottom: open ? "1px solid rgba(0,0,0,0.06)" : "none",
          borderLeft: `4px solid ${color}`,
          cursor: collapsible ? "pointer" : "default",
          userSelect: collapsible ? "none" : "auto",
        }}
      >
        <Box sx={{ color, display: "flex", alignItems: "center", "& svg": { fontSize: 20 } }}>{icon}</Box>
        <Box sx={{ flex: 1 }}>
          <Typography sx={{ fontSize: 14, fontWeight: 600, lineHeight: 1.3 }}>{title}</Typography>
          {subtitle && <Typography sx={{ fontSize: 11.5, color: "text.secondary", mt: 0.1 }}>{subtitle}</Typography>}
        </Box>
        {action}
        {collapsible && (open ? <ExpandLessIcon sx={{ color: "text.disabled", fontSize: 20 }} /> : <ExpandMoreIcon sx={{ color: "text.disabled", fontSize: 20 }} />)}
      </Box>
      <Collapse in={open}>
        <Box sx={{ p: 3 }}>{children}</Box>
      </Collapse>
    </Box>
  );
};

// ── Field Label ──
const FieldLabel: React.FC<{ label: string; required?: boolean }> = ({ label, required }) => (
  <Typography sx={{ fontSize: 12.5, fontWeight: 500, color: "text.secondary", mb: 0.5 }}>
    {label}{required && <Typography component="span" sx={{ color: "error.main", ml: 0.25 }}>*</Typography>}
  </Typography>
);

// ── Quick-add Modal ──
const QuickAddModal: React.FC<{
  open: boolean; onClose: () => void; title: string;
  onSave: (name: string) => Promise<void>; loading?: boolean;
}> = ({ open, onClose, title, onSave, loading }) => {
  const [name, setName] = useState("");
  const handleSave = async () => { if (name.trim()) { await onSave(name.trim()); setName(""); } };
  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth PaperProps={{ sx: { borderRadius: "14px" } }}>
      <DialogTitle sx={{ fontSize: 15, fontWeight: 600 }}>{title}</DialogTitle>
      <DialogContent>
        <TextField autoFocus size="small" fullWidth placeholder="Name" value={name}
          onChange={(e) => setName(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter") handleSave(); }}
          sx={{ mt: 1 }} />
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} sx={{ textTransform: "none", fontSize: 13 }}>Cancel</Button>
        <Button variant="contained" disabled={!name.trim() || loading} onClick={handleSave}
          sx={{ textTransform: "none", fontSize: 13, borderRadius: "10px", boxShadow: "none" }}>
          {loading ? <CircularProgress size={16} /> : "Save"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

// ── Main Component ──
const Index = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const { showErrorMessage, showMessage } = useNotifier();

  // Image state
  const [selectedImage, setSelectedImage] = useState<File | undefined>(undefined);
  const [isImageUploading, setIsImageUploading] = useState(false);
  const [isEnhancing, setIsEnhancing] = useState(false);
  const [isEnhanced, setIsEnhanced] = useState(false);
  const [cameraOpen, setCameraOpen] = useState(false);
  const [cropOpen, setCropOpen] = useState(false);
  const [cropImageSrc, setCropImageSrc] = useState<string | null>(null);
  const fileInputRef = React.useRef<HTMLInputElement>(null);

  // Portal access state
  const [portalEnabled, setPortalEnabled] = useState(true);
  const [portalUsername, setPortalUsername] = useState("");
  const [portalUsernameManual, setPortalUsernameManual] = useState(false);
  const [portalEmail, setPortalEmail] = useState("");
  const [portalEmailManual, setPortalEmailManual] = useState(false);
  const [portalRoleId, setPortalRoleId] = useState<number | null>(null);
  const [portalError, setPortalError] = useState("");
  const [portalPassword, setPortalPassword] = useState(generatePassword());
  const [showPassword, setShowPassword] = useState(false);

  // Duplicate check state
  const [emailExistsError, setEmailExistsError] = useState("");
  const [phoneExistsError, setPhoneExistsError] = useState("");

  // Multi-select location
  const [selectedLocationIds, setSelectedLocationIds] = useState<number[]>([]);

  // Quick-add modals
  const [deptModalOpen, setDeptModalOpen] = useState(false);
  const [posModalOpen, setPosModalOpen] = useState(false);

  // API hooks
  const [newCompany, { isLoading }] = useCreateTeamMutation();
  const [enumFindOrCreate] = useEnumFindOrCreateMutation();
  const [checkStaffExists] = useCheckStaffExistsMutation();
  const [createDepartment, { isLoading: deptCreating }] = useCreateDepartmentMutation();
  const [createPosition, { isLoading: posCreating }] = useCreatePositionMutation();
  const { data: staffIdData } = useGenerateStaffIdQuery();
  const { data: staffUsernameData } = useGenerateStaffUsernameQuery();
  const { data: provinceData } = useGetallEnumSearchQuery({ searchText: "", enum_type: "province" });
  const { data: countryData } = useGetallEnumSearchQuery({ searchText: "", enum_type: "country" });
  const { data: cityData } = useGetallEnumSearchQuery({ searchText: "", enum_type: "city" });
  const { data: locationData, isLoading: locationIsLoading } = useGetallLocationQuery({ searchText: "" });
  const { data: departmentData, isLoading: departmentLoading, refetch: refetchDepartments } = useGetDepartmentQuery({ searchText: "" });
  const { data: positionData, isLoading: positionLoading, refetch: refetchPositions } = useGetPositionQuery({ searchText: "" });
  const { data: roleData } = useGetUserRoleQuery({ searchText: "" });

  // Memoized lists
  const provinceList = useMemo(() => (provinceData?.data ?? []) as IEnumType[], [provinceData]);
  const countryList = useMemo(() => (countryData?.data ?? []) as IEnumType[], [countryData]);
  const cityList = useMemo(() => (cityData?.data ?? []) as IEnumType[], [cityData]);
  const locationList = useMemo(() => (locationData?.data ?? []) as IStudentLocation[], [locationData]);
  const departmentList = useMemo(() => (departmentData?.data ?? []) as IDepartment[], [departmentData]);
  const positionList = useMemo(() => (positionData?.data ?? []) as IPosition[], [positionData]);
  const staffRoles = useMemo(() => {
    const roles = (roleData?.data ?? []) as any[];
    return roles.filter((r: any) => r.roleName !== "Parent" && !r.isDelete);
  }, [roleData]);

  // Autocomplete options
  const provinceOptions = useMemo(() => provinceList.map((c) => ({ label: c.enum_value, value: c.id })), [provinceList]);
  const countryOptions = useMemo(() => countryList.map((c) => ({ label: c.enum_value, value: c.id })), [countryList]);
  const cityOptions = useMemo(() => cityList.map((c) => ({ label: c.enum_value, value: c.id })), [cityList]);
  const locationOptions = useMemo(() => locationList.map((c) => ({ label: c.location_name || c.location_address, value: c.id })), [locationList]);
  const departmentOptions = useMemo(() => (departmentList || []).map((c) => ({ label: c.departmentName, value: c.id })), [departmentList]);
  const positionOptions = useMemo(() => (positionList || []).map((c) => ({ label: c.positionName, value: c.id })), [positionList]);
  const genderOptions: CustomerOptionString[] = Object.values(Gender).map((g) => ({ label: g, value: g }));

  // Phone formatter — `formatPhoneNumber` caps at 10 digits and formats
  // progressively (647 → 647, 6475 → 647-5, 6475675666 → 647-567-5666).
  // The older `formatPhoneNumberForDisplay` returned the raw value when
  // the digit count wasn't exactly 10, which let 20+ digits through the
  // input and produced the "6478999999999999999993" screenshot.
  const handlePhoneChange = (field: string, value: string, setFieldValue: (f: string, v: any) => void) => {
    setFieldValue(field, formatPhoneNumber(value));
  };

  // Auto-set generated Staff ID
  useEffect(() => {
    if (staffIdData?.data) {
      formik.setFieldValue("teamId", staffIdData.data as string);
    }
  }, [staffIdData]);

  // ── Duplicate check handlers ──
  const handleEmailBlur = async (email: string) => {
    if (!email || validateEmail(email)) return;
    try {
      const res = await checkStaffExists({ email: email.trim().toLowerCase() }).unwrap();
      if (res.data && (res.data as any).email_exists) {
        setEmailExistsError("Email already exists");
      } else {
        setEmailExistsError("");
      }
    } catch {
      setEmailExistsError("");
    }
  };

  const handlePhoneBlur = async (phone: string) => {
    if (!phone || validatePhoneNumber(phone)) return;
    try {
      const res = await checkStaffExists({ phone: phone.replace(/-/g, "") }).unwrap();
      if (res.data && (res.data as any).phone_exists) {
        setPhoneExistsError("Phone number already exists");
      } else {
        setPhoneExistsError("");
      }
    } catch {
      setPhoneExistsError("");
    }
  };

  // ── Quick-add handlers ──
  const handleAddDepartment = async (name: string) => {
    try {
      const res = await createDepartment({ departmentName: name }).unwrap();
      if (res.status && res.data) {
        refetchDepartments();
        formik.setFieldValue("teamDepartmentId", (res.data as any).id);
        showMessage("Department created");
      } else {
        showErrorMessage(res.message || "Failed to create department");
      }
    } catch { showErrorMessage("Failed to create department"); }
    setDeptModalOpen(false);
  };

  const handleAddPosition = async (name: string) => {
    try {
      const res = await createPosition({ positionName: name }).unwrap();
      if (res.status && res.data) {
        refetchPositions();
        formik.setFieldValue("teamPositionId", (res.data as any).id);
        showMessage("Position created");
      } else {
        showErrorMessage(res.message || "Failed to create position");
      }
    } catch { showErrorMessage("Failed to create position"); }
    setPosModalOpen(false);
  };

  // Formik
  const formik = useFormik<ITeamCreate>({
    initialValues: {
      teamMobileNum: "", teamId: "", teamPositionId: "", teamHourlySalary: "",
      teamOtHourlySalary: "", teamDepartmentId: "", teamFirstName: "", teamLastName: "",
      teamAltMobileNum: "", teamImg: "", teamCity: "", teamAddress: "", teamEmail: "",
      teamDescription: "", teamUserName: "", teamPassWord: "", teamStreet: "",
      teamProvince: "", teamPostalCode: "", emailReminder: true, smsReminder: true,
      availability: [
        { day: "Sunday", timeSlots: [] }, { day: "Monday", timeSlots: [] },
        { day: "Tuesday", timeSlots: [] }, { day: "Wednesday", timeSlots: [] },
        { day: "Thursday", timeSlots: [] }, { day: "Friday", timeSlots: [] },
        { day: "Saturday", timeSlots: [] },
      ],
      middle_name: "", location_id: "", gender: "", mode: "",
      unit_number: "", street: "", route: "", city_id: "", province_id: "",
      postal_code: "", country_id: "", address_id: "",
      is_class_reminder: true, email_credential_reminder: false, sms_credential_reminder: false,
    },
    enableReinitialize: true,
    validate: (values) => {
      const errors: { [key: string]: string } = {};
      if (!values.teamFirstName?.trim()) errors.teamFirstName = "First name is required";
      if (!values.teamLastName?.trim()) errors.teamLastName = "Last name is required";
      if (values.teamEmail) { const e = validateEmail(values.teamEmail); if (e) errors.teamEmail = e; }
      if (values.teamMobileNum) { const e = validatePhoneNumber(values.teamMobileNum); if (e) errors.teamMobileNum = e; }
      if (values.teamAltMobileNum) { const e = validatePhoneNumber(values.teamAltMobileNum); if (e) errors.teamAltMobileNum = e; }
      return errors;
    },
    onSubmit: async (values, { resetForm, setFieldError, setFieldTouched }) => {
      if (portalEnabled) {
        const email = portalEmail || values.teamEmail?.trim().toLowerCase();
        if (!email) { setPortalError("Email is required for portal access."); showErrorMessage("Email is required for portal access."); return; }
        setPortalError("");
      }
      try {
        let teamImg: string | undefined;
        if (selectedImage) { setIsImageUploading(true); teamImg = (await uploadImage(selectedImage)) || ""; setIsImageUploading(false); }
        const payload = {
          teamMobileNum: values.teamMobileNum.replace(/-/g, ""),
          teamAltMobileNum: values.teamAltMobileNum.replace(/-/g, ""),
          teamId: values.teamId, teamPositionId: values.teamPositionId,
          teamHourlySalary: String(values.teamHourlySalary || ""), teamOtHourlySalary: String(values.teamOtHourlySalary || ""),
          teamDepartmentId: values.teamDepartmentId,
          teamFirstName: toProperCase(values.teamFirstName), teamLastName: toProperCase(values.teamLastName),
          teamCity: toProperCase(values.teamCity), teamAddress: values.teamAddress,
          teamEmail: values.teamEmail?.trim().toLowerCase(), teamDescription: values.teamDescription,
          teamUserName: values.teamUserName, teamPassWord: values.teamPassWord,
          teamStreet: toProperCase(values.teamStreet), teamProvince: toProperCase(values.teamProvince),
          teamPostalCode: toAllCaps(values.teamPostalCode), teamImg,
          availability: values.availability, emailReminder: values.emailReminder, smsReminder: values.smsReminder,
          middle_name: toProperCase(values.middle_name), gender: values.gender, mode: values.mode,
          location_ids: selectedLocationIds,
          unit_number: values.unit_number, street: values.street,
          route: values.route,
          city_id: values.city_id ? Number(values.city_id) : null,
          province_id: values.province_id ? Number(values.province_id) : null,
          postal_code: toAllCaps(values.postal_code),
          country_id: values.country_id ? Number(values.country_id) : null,
          address_id: values.address_id ? Number(values.address_id) : null,
          sms_credential_reminder: values.sms_credential_reminder,
          email_credential_reminder: values.email_credential_reminder,
          is_class_reminder: values.is_class_reminder,
          notifications: { class_reminders: true },
          portal_access_enabled: portalEnabled,
          portal_username: portalEnabled ? portalUsername.trim() : undefined,
          portal_email: portalEnabled ? (portalEmail || values.teamEmail?.trim().toLowerCase()) : undefined,
          portal_role_id: portalEnabled ? portalRoleId : undefined,
          password: portalEnabled ? portalPassword : undefined,
        };
        const response = await newCompany(payload).unwrap();
        if (!response.status) {
          // Route field-level messages into inline form errors so the
          // admin sees "This email address is already used by another
          // account." directly under the Email input, not just a top
          // toast. Falls back to a toast for unfielded errors.
          const errs = (response as any)?.errors;
          let fielded = false;
          if (errs && typeof errs === "object") {
            Object.entries(errs).forEach(([field, msgs]) => {
              const first = Array.isArray(msgs) ? msgs[0] : msgs;
              if (typeof first === "string" && first.trim() !== "") {
                setFieldError(field, first);
                setFieldTouched(field, true, false);
                fielded = true;
              }
            });
          }
          if (!fielded) showErrorMessage(response.message);
        }
        else { showMessage("Staff created successfully"); navigate(`/team-history/${response.data.id}`); resetForm(); setSelectedImage(undefined); setIsEnhanced(false); setSelectedLocationIds([]); }
      } catch (err: any) {
        const errs = err?.data?.errors;
        if (errs && typeof errs === "object") {
          Object.entries(errs).forEach(([field, msgs]) => {
            const first = Array.isArray(msgs) ? msgs[0] : msgs;
            if (typeof first === "string" && first.trim() !== "") {
              setFieldError(field, first);
              setFieldTouched(field, true, false);
            }
          });
        }
        const msg = err?.data?.message || err?.message || "Something went wrong";
        showErrorMessage(msg);
      }
    },
  });

  const formValid = useMemo(() => {
    return !!(
      formik.values.teamFirstName?.trim() &&
      formik.values.teamLastName?.trim() &&
      !emailExistsError &&
      !phoneExistsError
    );
  }, [formik.values.teamFirstName, formik.values.teamLastName, emailExistsError, phoneExistsError]);

  // Auto-sync portal email
  useEffect(() => {
    if (portalEnabled && !portalEmailManual && formik.values.teamEmail) {
      setPortalEmail(formik.values.teamEmail.trim().toLowerCase());
    }
  }, [formik.values.teamEmail, portalEnabled, portalEmailManual]);

  // New staff are always assigned the "Staff" role — no role picker needed.
  useEffect(() => {
    const staffRole = staffRoles.find((r: any) => r.roleName === "Staff");
    if (staffRole) setPortalRoleId(staffRole.id);
  }, [staffRoles]);

  // Auto-fill the login username (STS####); editable if they want a custom one.
  useEffect(() => {
    if (staffUsernameData?.data && !portalUsernameManual) {
      setPortalUsername(staffUsernameData.data as string);
    }
  }, [staffUsernameData, portalUsernameManual]);

  // Auto-enhance helper
  const applyEnhancement = async (file: File) => {
    setIsEnhancing(true);
    setIsEnhanced(false);
    try {
      const enhanced = await enhanceImage(file);
      setSelectedImage(enhanced);
      setIsEnhanced(true);
    } catch {
      setSelectedImage(file);
    } finally {
      setIsEnhancing(false);
    }
  };

  // Image handlers
  const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.match(/^image\/(jpeg|png)$/)) { showErrorMessage("Only JPEG and PNG images are accepted"); return; }
    if (file.size > 5 * 1024 * 1024) { showErrorMessage("File must be under 5 MB"); return; }
    const reader = new FileReader();
    reader.onload = () => { setCropImageSrc(reader.result as string); setCropOpen(true); };
    reader.readAsDataURL(file);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };
  const handleCameraCapture = (dataUrl: string) => { setCropImageSrc(dataUrl); setCropOpen(true); };
  const handleCropComplete = (croppedFile: File) => applyEnhancement(croppedFile);

  // Address auto-fill
  const handleAddressAutoFill = useCallback(
    async (addr: ParsedAddress) => {
      if (addr.street) formik.setFieldValue("street", addr.street);
      if (addr.postalCode) formik.setFieldValue("postal_code", addr.postalCode.toUpperCase());

      const findMatch = (options: CustomerOption[], candidates: string[]) => {
        for (const candidate of candidates) {
          if (!candidate) continue;
          const lower = candidate.toLowerCase().trim();
          for (const opt of options) {
            const optLower = opt.label.toLowerCase().trim();
            if (optLower === lower || optLower.includes(lower) || lower.includes(optLower)) return opt;
          }
        }
        return null;
      };

      // City
      const cityCandidates = [addr.suburb, addr.city].filter(Boolean);
      const cityMatch = findMatch(cityOptions, cityCandidates);
      if (cityMatch) {
        formik.setFieldValue("city_id", String(cityMatch.value));
      } else {
        const cityName = addr.suburb || addr.city;
        if (cityName) {
          try {
            const r = await enumFindOrCreate({ enum_type: "city", enum_value: cityName }).unwrap();
            if (r.status && r.data) formik.setFieldValue("city_id", String((r.data as any).id));
          } catch {}
        }
      }

      // Province
      if (addr.province) {
        const provMatch = findMatch(provinceOptions, [addr.province]);
        if (provMatch) {
          formik.setFieldValue("province_id", String(provMatch.value));
        } else {
          try {
            const r = await enumFindOrCreate({ enum_type: "province", enum_value: addr.province }).unwrap();
            if (r.status && r.data) formik.setFieldValue("province_id", String((r.data as any).id));
          } catch {}
        }
      }

      // Country
      if (addr.country) {
        const countryMatch = findMatch(countryOptions, [addr.country]);
        if (countryMatch) {
          formik.setFieldValue("country_id", String(countryMatch.value));
        } else {
          try {
            const r = await enumFindOrCreate({ enum_type: "country", enum_value: addr.country }).unwrap();
            if (r.status && r.data) formik.setFieldValue("country_id", String((r.data as any).id));
          } catch {}
        }
      }
    },
    [cityOptions, provinceOptions, countryOptions, formik, enumFindOrCreate]
  );

  if (locationIsLoading || departmentLoading || positionLoading) {
    return <Box display="flex" justifyContent="center" alignItems="center" height="30vh"><StepUpSpinner size="medium" /></Box>;
  }

  return (
    <Box sx={{ p: { xs: 1, md: 2 } }}>
      <FormikProvider value={formik}>
        <form onSubmit={formik.handleSubmit}>
          {/* ── Header ── */}
          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 3 }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
              <Button size="small" startIcon={<ArrowBackIcon />} onClick={() => navigate("/team/list")}
                sx={{ textTransform: "none", color: "text.secondary", fontSize: 13 }}>
                Back
              </Button>
              <Typography sx={{ fontSize: 20, fontWeight: 700 }}>Add New Staff</Typography>
            </Box>
            <Box sx={{ display: "flex", gap: 1 }}>
              <Button variant="outlined" onClick={() => { formik.resetForm(); setSelectedImage(undefined); setIsEnhanced(false); setSelectedLocationIds([]); setEmailExistsError(""); setPhoneExistsError(""); setPortalPassword(generatePassword()); }}
                sx={{ borderRadius: "10px", textTransform: "none", fontSize: 13, borderColor: "rgba(0,0,0,0.12)", color: "text.secondary" }}>
                Reset
              </Button>
              <Button variant="contained" startIcon={isLoading ? <CircularProgress size={16} color="inherit" /> : <SaveOutlinedIcon />}
                onClick={() => formik.handleSubmit()} disabled={!formValid || isLoading}
                sx={{ borderRadius: "10px", textTransform: "none", fontSize: 13, fontWeight: 600, boxShadow: "none", px: 3 }}>
                Save Staff
              </Button>
            </Box>
          </Box>

          <Box sx={{ display: "flex", flexDirection: "column", gap: 2.5 }}>

            {/* ═══ SECTION 1: Profile Setup ═══ */}
            <SectionCard icon={<PersonOutlineIcon />} title="Profile Setup" subtitle="Name, gender, and photo">
              <Grid container spacing={3}>
                {/* Photo */}
                <Grid item lg={3} md={4} sm={12} xs={12}>
                  <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 1.5 }}>
                    <Box sx={{
                      position: "relative", width: 140, height: 140, borderRadius: "50%",
                      border: selectedImage ? `3px solid ${theme.palette.primary.main}` : "2px dashed rgba(0,0,0,0.12)",
                      display: "flex", alignItems: "center", justifyContent: "center", overflow: "hidden", bgcolor: "rgba(0,0,0,0.02)",
                    }}>
                      {isEnhancing ? (
                        <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 0.5 }}>
                          <CircularProgress size={28} />
                          <Typography sx={{ fontSize: 9.5, color: "text.disabled" }}>Enhancing...</Typography>
                        </Box>
                      ) : selectedImage ? (
                        <>
                          <img src={URL.createObjectURL(selectedImage)} alt="Preview" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                          <Box onClick={() => { setCropImageSrc(URL.createObjectURL(selectedImage)); setCropOpen(true); }}
                            sx={{ position: "absolute", inset: 0, bgcolor: "rgba(0,0,0,0.4)", display: "flex", alignItems: "center", justifyContent: "center", opacity: 0, transition: "opacity 0.2s", cursor: "pointer", "&:hover": { opacity: 1 } }}>
                            <EditOutlinedIcon sx={{ color: "#fff", fontSize: 22 }} />
                          </Box>
                        </>
                      ) : (
                        <Box sx={{ textAlign: "center" }}>
                          <CloudUploadIcon sx={{ fontSize: 28, color: "rgba(0,0,0,0.18)" }} />
                          <Typography sx={{ fontSize: 10.5, color: "text.disabled" }}>No Photo</Typography>
                        </Box>
                      )}
                    </Box>
                    {isEnhanced && selectedImage && (
                      <Chip label="Auto Enhanced" size="small"
                        sx={{ fontSize: 10, height: 20, bgcolor: alpha("#22c55e", 0.1), color: "#16a34a", fontWeight: 600 }} />
                    )}
                    <Box sx={{ display: "flex", gap: 1 }}>
                      <Button size="small" variant="outlined" startIcon={<CameraAltIcon sx={{ fontSize: "14px !important" }} />}
                        onClick={() => setCameraOpen(true)} disabled={isEnhancing}
                        sx={{ borderRadius: "8px", textTransform: "none", fontSize: 11.5, py: 0.4, borderColor: "rgba(0,0,0,0.12)", color: "text.secondary" }}>
                        Camera
                      </Button>
                      <Button size="small" variant="outlined" startIcon={<CloudUploadIcon sx={{ fontSize: "14px !important" }} />}
                        onClick={() => fileInputRef.current?.click()} disabled={isEnhancing}
                        sx={{ borderRadius: "8px", textTransform: "none", fontSize: 11.5, py: 0.4, borderColor: "rgba(0,0,0,0.12)", color: "text.secondary" }}>
                        Upload
                      </Button>
                    </Box>
                    {selectedImage && !isEnhancing && (
                      <Button size="small" onClick={() => { setSelectedImage(undefined); setIsEnhanced(false); }}
                        sx={{ fontSize: 11, textTransform: "none", color: "text.disabled" }}>Remove</Button>
                    )}
                    <Typography sx={{ fontSize: 10, color: "text.disabled", textAlign: "center" }}>
                      Use good lighting for best results
                    </Typography>
                    <input ref={fileInputRef} accept="image/jpeg,image/png" type="file" onChange={handleImageChange} style={{ display: "none" }} />
                  </Box>
                </Grid>
                {/* Name Fields */}
                <Grid item lg={9} md={8} sm={12} xs={12}>
                  <Grid container spacing={2}>
                    <Grid item lg={4} md={4} sm={6} xs={12}>
                      <FieldLabel label="First Name" required />
                      <TextField size="small" fullWidth placeholder="First name" {...formik.getFieldProps("teamFirstName")}
                        error={!!formik.touched.teamFirstName && !!formik.errors.teamFirstName}
                        helperText={formik.touched.teamFirstName && formik.errors.teamFirstName} />
                    </Grid>
                    <Grid item lg={4} md={4} sm={6} xs={12}>
                      <FieldLabel label="Middle Name" />
                      <TextField size="small" fullWidth placeholder="Middle name" {...formik.getFieldProps("middle_name")} />
                    </Grid>
                    <Grid item lg={4} md={4} sm={6} xs={12}>
                      <FieldLabel label="Last Name" required />
                      <TextField size="small" fullWidth placeholder="Last name" {...formik.getFieldProps("teamLastName")}
                        error={!!formik.touched.teamLastName && !!formik.errors.teamLastName}
                        helperText={formik.touched.teamLastName && formik.errors.teamLastName} />
                    </Grid>
                    <Grid item lg={4} md={4} sm={6} xs={12}>
                      <FieldLabel label="Gender" />
                      <Autocomplete
                        options={genderOptions}
                        getOptionLabel={(o: CustomerOptionString) => o.label}
                        value={genderOptions.find((o) => o.value === formik.values.gender) || null}
                        onChange={(_, v) => formik.setFieldValue("gender", v?.value || "")}
                        renderInput={(params) => <TextField {...params} placeholder="Select" size="small" />}
                      />
                    </Grid>
                  </Grid>
                </Grid>
              </Grid>
            </SectionCard>

            {/* ═══ SECTION 2: Contact Info ═══ */}
            <SectionCard icon={<PhoneIcon />} title="Contact Information" subtitle="Phone numbers and email" accentColor="#3b82f6">
              <Grid container spacing={2}>
                <Grid item lg={4} md={4} sm={6} xs={12}>
                  <FieldLabel label="Main Phone" />
                  <TextField size="small" fullWidth placeholder="000-000-0000"
                    value={formik.values.teamMobileNum}
                    onChange={(e) => { handlePhoneChange("teamMobileNum", e.target.value, formik.setFieldValue); setPhoneExistsError(""); }}
                    onBlur={() => handlePhoneBlur(formik.values.teamMobileNum)}
                    error={!!formik.errors.teamMobileNum || !!phoneExistsError}
                    helperText={formik.errors.teamMobileNum || phoneExistsError}
                    InputProps={{ startAdornment: <InputAdornment position="start"><PhoneIcon sx={{ fontSize: 16, color: "text.disabled" }} /></InputAdornment> }}
                  />
                </Grid>
                <Grid item lg={4} md={4} sm={6} xs={12}>
                  <FieldLabel label="Emergency Phone" />
                  <TextField size="small" fullWidth placeholder="000-000-0000"
                    value={formik.values.teamAltMobileNum}
                    onChange={(e) => handlePhoneChange("teamAltMobileNum", e.target.value, formik.setFieldValue)}
                    error={!!formik.errors.teamAltMobileNum} helperText={formik.errors.teamAltMobileNum}
                  />
                </Grid>
                <Grid item lg={4} md={4} sm={6} xs={12}>
                  <FieldLabel label="Email" />
                  <TextField size="small" fullWidth placeholder="email@example.com" type="email"
                    {...formik.getFieldProps("teamEmail")}
                    onChange={(e) => { formik.setFieldValue("teamEmail", e.target.value); setEmailExistsError(""); }}
                    onBlur={() => handleEmailBlur(formik.values.teamEmail)}
                    error={!!formik.errors.teamEmail || !!emailExistsError}
                    helperText={formik.errors.teamEmail || emailExistsError}
                  />
                </Grid>
              </Grid>
            </SectionCard>

            {/* ═══ SECTION 3: Address (collapsible) ═══ */}
            <SectionCard icon={<HomeOutlinedIcon />} title="Address" subtitle="Staff member's home address"
              accentColor="#8b5cf6" collapsible defaultOpen={false}>
              <Box sx={{ mb: 2.5 }}>
                <AddressAutocomplete
                  placeholder="Start typing an address to auto-fill..."
                  countryCode="ca"
                  onAddressSelect={(addr: ParsedAddress) => handleAddressAutoFill(addr)}
                />
                <Typography sx={{ fontSize: 10.5, color: "text.disabled", mt: 0.5 }}>
                  Select a result to auto-fill the fields below
                </Typography>
              </Box>
              <Divider sx={{ mb: 2.5 }} />
              <Grid container spacing={2}>
                <Grid item lg={2} md={3} sm={6} xs={12}>
                  <FieldLabel label="Unit #" />
                  <TextField size="small" fullWidth placeholder="Apt/Unit" {...formik.getFieldProps("unit_number")} />
                </Grid>
                <Grid item lg={4} md={4} sm={6} xs={12}>
                  <FieldLabel label="Street" />
                  <TextField size="small" fullWidth placeholder="Street address" {...formik.getFieldProps("street")} />
                </Grid>
                <Grid item lg={3} md={3} sm={6} xs={12}>
                  <FieldLabel label="City" />
                  <Autocomplete
                    options={cityOptions} getOptionLabel={(o: any) => o.label}
                    value={cityOptions.find((o) => o.value === Number(formik.values.city_id)) || null}
                    onChange={(_, v) => formik.setFieldValue("city_id", v?.value || "")}
                    renderInput={(params) => <TextField {...params} placeholder="Select" size="small" />}
                  />
                </Grid>
                <Grid item lg={3} md={3} sm={6} xs={12}>
                  <FieldLabel label="Province" />
                  <Autocomplete
                    options={provinceOptions} getOptionLabel={(o: any) => o.label}
                    value={provinceOptions.find((o) => o.value === Number(formik.values.province_id)) || null}
                    onChange={(_, v) => formik.setFieldValue("province_id", v?.value || "")}
                    renderInput={(params) => <TextField {...params} placeholder="Select" size="small" />}
                  />
                </Grid>
                <Grid item lg={3} md={3} sm={6} xs={12}>
                  <FieldLabel label="Postal Code" />
                  <TextField size="small" fullWidth placeholder="A1A 1A1" {...formik.getFieldProps("postal_code")} />
                </Grid>
                <Grid item lg={3} md={3} sm={6} xs={12}>
                  <FieldLabel label="Country" />
                  <Autocomplete
                    options={countryOptions} getOptionLabel={(o: any) => o.label}
                    value={countryOptions.find((o) => o.value === Number(formik.values.country_id)) || null}
                    onChange={(_, v) => formik.setFieldValue("country_id", v?.value || "")}
                    renderInput={(params) => <TextField {...params} placeholder="Select" size="small" />}
                  />
                </Grid>
              </Grid>
            </SectionCard>

            {/* ═══ SECTION 4: Work Settings ═══ */}
            <SectionCard icon={<WorkOutlineIcon />} title="Work Settings" subtitle="Mode, location, and notification preferences" accentColor="#f59e0b">
              <Grid container spacing={2.5}>
                <Grid item lg={4} md={4} sm={6} xs={12}>
                  <FieldLabel label="Teaching Mode" />
                  <Box sx={{ display: "flex", gap: 1 }}>
                    {[{ label: "In Person", value: "InPerson" }, { label: "Online", value: "Online" }].map((opt) => {
                      const modes = (formik.values.mode || "").split(",").filter(Boolean);
                      const active = modes.includes(opt.value);
                      const toggle = () => {
                        const updated = active ? modes.filter((m) => m !== opt.value) : [...modes, opt.value];
                        formik.setFieldValue("mode", updated.join(","));
                      };
                      return (
                        <Box key={opt.value} onClick={toggle}
                          sx={{
                            flex: 1, py: 1, textAlign: "center", borderRadius: "10px", cursor: "pointer",
                            border: `1.5px solid ${active ? theme.palette.primary.main : "rgba(0,0,0,0.1)"}`,
                            bgcolor: active ? alpha(theme.palette.primary.main, 0.06) : "transparent",
                            color: active ? theme.palette.primary.main : "text.secondary",
                            fontSize: 12.5, fontWeight: active ? 600 : 500, transition: "all 0.15s",
                          }}>
                          {opt.label}
                        </Box>
                      );
                    })}
                  </Box>
                </Grid>
                <Grid item lg={4} md={4} sm={6} xs={12}>
                  <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                    <FieldLabel label="Location" />
                    {locationOptions.length > 0 && (
                      <Link component="button" type="button" underline="hover" sx={{ fontSize: 12 }}
                        onClick={() =>
                          setSelectedLocationIds(
                            selectedLocationIds.length === locationOptions.length
                              ? []
                              : locationOptions.map((o) => o.value)
                          )
                        }>
                        {selectedLocationIds.length === locationOptions.length ? "Clear All" : "Select All"}
                      </Link>
                    )}
                  </Box>
                  <Autocomplete
                    multiple
                    options={locationOptions}
                    getOptionLabel={(o: any) => o.label}
                    value={locationOptions.filter((o) => selectedLocationIds.includes(o.value))}
                    onChange={(_, v) => setSelectedLocationIds(v.map((item: any) => item.value))}
                    disableCloseOnSelect
                    renderOption={(props, option, { selected }) => (
                      <li {...props}>
                        <Checkbox size="small" checked={selected} sx={{ mr: 1 }} />
                        {option.label}
                      </li>
                    )}
                    renderTags={(value, getTagProps) =>
                      value.map((option: any, index: number) => (
                        <Chip {...getTagProps({ index })} key={option.value} label={option.label} size="small"
                          sx={{ borderRadius: "8px", fontSize: 11.5, height: 24 }} />
                      ))
                    }
                    renderInput={(params) => <TextField {...params} placeholder={selectedLocationIds.length ? "" : "Select locations"} size="small" />}
                  />
                </Grid>
                <Grid item lg={4} md={4} sm={12} xs={12}>
                  <FieldLabel label="Notifications" />
                  <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
                    <FormControlLabel sx={{ m: 0, "& .MuiTypography-root": { fontSize: 12.5 } }}
                      control={<Switch size="small" checked={formik.values.emailReminder} onChange={(e) => formik.setFieldValue("emailReminder", e.target.checked)} />}
                      label="Email reminders" />
                    <FormControlLabel sx={{ m: 0, "& .MuiTypography-root": { fontSize: 12.5 } }}
                      control={<Switch size="small" checked={formik.values.smsReminder} onChange={(e) => formik.setFieldValue("smsReminder", e.target.checked)} />}
                      label="SMS reminders" />
                    <FormControlLabel sx={{ m: 0, "& .MuiTypography-root": { fontSize: 12.5 } }}
                      control={<Switch size="small" checked={formik.values.is_class_reminder} onChange={(e) => formik.setFieldValue("is_class_reminder", e.target.checked)} />}
                      label="Class reminders" />
                  </Box>
                </Grid>
              </Grid>
            </SectionCard>

            {/* ═══ SECTION 5: Employment Details ═══ */}
            <SectionCard icon={<BadgeOutlinedIcon />} title="Employment Details" subtitle="ID, department, position, and salary" accentColor="#22c55e">
              <Grid container spacing={2}>
                <Grid item lg={4} md={4} sm={6} xs={12}>
                  <FieldLabel label="Staff ID" />
                  <TextField size="small" fullWidth placeholder="Auto-generated"
                    value={formik.values.teamId} InputProps={{ readOnly: true }}
                    sx={{ "& .MuiInputBase-input": { bgcolor: "rgba(0,0,0,0.02)" } }} />
                </Grid>
                <Grid item lg={4} md={4} sm={6} xs={12}>
                  <FieldLabel label="Department" />
                  <Box sx={{ display: "flex", gap: 0.5 }}>
                    <Autocomplete
                      sx={{ flex: 1 }}
                      options={departmentOptions} getOptionLabel={(o: any) => o.label}
                      value={departmentOptions.find((o) => o.value === Number(formik.values.teamDepartmentId)) || null}
                      onChange={(_, v) => formik.setFieldValue("teamDepartmentId", v?.value || "")}
                      renderInput={(params) => <TextField {...params} placeholder="Select" size="small" />}
                    />
                    <IconButton size="small" onClick={() => setDeptModalOpen(true)}
                      sx={{ border: "1px solid rgba(0,0,0,0.12)", borderRadius: "8px", width: 36, height: 36 }}>
                      <AddIcon sx={{ fontSize: 18 }} />
                    </IconButton>
                  </Box>
                </Grid>
                <Grid item lg={4} md={4} sm={6} xs={12}>
                  <FieldLabel label="Position" />
                  <Box sx={{ display: "flex", gap: 0.5 }}>
                    <Autocomplete
                      sx={{ flex: 1 }}
                      options={positionOptions} getOptionLabel={(o: any) => o.label}
                      value={positionOptions.find((o) => o.value === Number(formik.values.teamPositionId)) || null}
                      onChange={(_, v) => formik.setFieldValue("teamPositionId", v?.value || "")}
                      renderInput={(params) => <TextField {...params} placeholder="Select" size="small" />}
                    />
                    <IconButton size="small" onClick={() => setPosModalOpen(true)}
                      sx={{ border: "1px solid rgba(0,0,0,0.12)", borderRadius: "8px", width: 36, height: 36 }}>
                      <AddIcon sx={{ fontSize: 18 }} />
                    </IconButton>
                  </Box>
                </Grid>
                <Grid item lg={3} md={4} sm={6} xs={12}>
                  <FieldLabel label="Hourly Salary ($)" />
                  <TextField size="small" fullWidth type="number" placeholder="0.00" {...formik.getFieldProps("teamHourlySalary")}
                    InputProps={{ startAdornment: <InputAdornment position="start">$</InputAdornment> }} />
                </Grid>
                <Grid item lg={3} md={4} sm={6} xs={12}>
                  <FieldLabel label="OT Hourly Salary ($)" />
                  <TextField size="small" fullWidth type="number" placeholder="0.00" {...formik.getFieldProps("teamOtHourlySalary")}
                    InputProps={{ startAdornment: <InputAdornment position="start">$</InputAdornment> }} />
                </Grid>
              </Grid>
            </SectionCard>

            {/* ═══ SECTION 6: Notes ═══ */}
            <SectionCard icon={<NotesOutlinedIcon />} title="Notes" subtitle="Internal notes about this staff member"
              accentColor="#6b7280" collapsible defaultOpen={false}>
              <TextField size="small" fullWidth multiline rows={3} placeholder="Add any notes about this staff member..."
                {...formik.getFieldProps("teamDescription")} />
            </SectionCard>

            {/* ═══ SECTION 7: Portal Access ═══ */}
            <SectionCard icon={<VpnKeyIcon />} title="Portal Access" subtitle="Allow this staff member to log into the platform"
              accentColor="#6366f1"
              action={
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }} onClick={(e) => e.stopPropagation()}>
                  <Switch size="small" checked={portalEnabled} onChange={(e) => setPortalEnabled(e.target.checked)} />
                  <Chip label={portalEnabled ? "Enabled" : "Disabled"} size="small"
                    sx={{ fontWeight: 600, fontSize: 10.5, height: 22,
                      bgcolor: portalEnabled ? alpha("#22c55e", 0.1) : alpha("#ef4444", 0.08),
                      color: portalEnabled ? "#16a34a" : "#dc2626",
                    }} />
                </Box>
              }>
              {portalEnabled ? (
                <Grid container spacing={2}>
                  <Grid item lg={4} md={4} sm={12} xs={12}>
                    <FieldLabel label="Username" required />
                    <TextField size="small" fullWidth placeholder="Auto-generated (e.g. STS0001)"
                      value={portalUsername}
                      onChange={(e) => { setPortalUsername(e.target.value); setPortalUsernameManual(true); setPortalError(""); }} />
                    <Typography sx={{ fontSize: 10.5, color: "text.disabled", mt: 0.5 }}>Used to log in. Auto-generated — edit if you want a custom one. Role is "Staff" automatically.</Typography>
                  </Grid>
                  <Grid item lg={4} md={4} sm={12} xs={12}>
                    <FieldLabel label="Email" required />
                    <TextField size="small" fullWidth placeholder="Auto-filled from staff email"
                      value={portalEmail || formik.values.teamEmail || ""}
                      onChange={(e) => { setPortalEmail(e.target.value); setPortalEmailManual(true); setPortalError(""); }}
                      error={!!portalError && !portalEmail && !formik.values.teamEmail} />
                    <Typography sx={{ fontSize: 10.5, color: "text.disabled", mt: 0.5 }}>Used for password resets and notifications.</Typography>
                  </Grid>
                  <Grid item lg={4} md={4} sm={12} xs={12}>
                    <FieldLabel label="Password" required />
                    <TextField size="small" fullWidth
                      type={showPassword ? "text" : "password"}
                      value={portalPassword}
                      onChange={(e) => setPortalPassword(e.target.value)}
                      InputProps={{
                        endAdornment: (
                          <InputAdornment position="end">
                            <IconButton size="small" onClick={() => setShowPassword(!showPassword)} edge="end">
                              {showPassword ? <VisibilityOffIcon sx={{ fontSize: 18 }} /> : <VisibilityIcon sx={{ fontSize: 18 }} />}
                            </IconButton>
                            <IconButton size="small" onClick={() => setPortalPassword(generatePassword())} edge="end" title="Regenerate password">
                              <RefreshIcon sx={{ fontSize: 18 }} />
                            </IconButton>
                          </InputAdornment>
                        ),
                      }}
                    />
                    <Typography sx={{ fontSize: 10.5, color: "text.disabled", mt: 0.5 }}>Auto-generated. You can edit before saving.</Typography>
                  </Grid>
                  {portalError && (
                    <Grid item xs={12}>
                      <Typography sx={{ fontSize: 12, color: "error.main", fontWeight: 600 }}>{portalError}</Typography>
                    </Grid>
                  )}
                </Grid>
              ) : (
                <Typography sx={{ fontSize: 12.5, color: "text.disabled" }}>
                  Enable the toggle above to configure login credentials for this staff member.
                </Typography>
              )}
            </SectionCard>
          </Box>

          {/* ── Sticky Footer ── */}
          <Box sx={{
            position: "sticky", bottom: 0, mt: 3, py: 2, px: 3,
            bgcolor: "#fff", borderRadius: "12px", border: "1px solid rgba(0,0,0,0.08)",
            boxShadow: "0 -2px 8px rgba(0,0,0,0.04)",
            display: "flex", justifyContent: "flex-end", gap: 1.5,
          }}>
            <Button variant="outlined" onClick={() => navigate("/team/list")}
              sx={{ borderRadius: "10px", textTransform: "none", fontSize: 13, borderColor: "rgba(0,0,0,0.12)", color: "text.secondary", px: 3 }}>
              Cancel
            </Button>
            <Button variant="contained" startIcon={isLoading ? <CircularProgress size={16} color="inherit" /> : <SaveOutlinedIcon />}
              onClick={() => formik.handleSubmit()} disabled={!formValid || isLoading}
              sx={{ borderRadius: "10px", textTransform: "none", fontSize: 13, fontWeight: 600, boxShadow: "none", px: 3 }}>
              Save Staff
            </Button>
          </Box>
        </form>
      </FormikProvider>

      {/* ── Modals ── */}
      <CameraModal open={cameraOpen} onClose={() => setCameraOpen(false)} onCapture={handleCameraCapture} />
      <CropModal open={cropOpen} imageSrc={cropImageSrc} onClose={() => { setCropOpen(false); setCropImageSrc(null); }} onCropComplete={handleCropComplete} />
      <QuickAddModal open={deptModalOpen} onClose={() => setDeptModalOpen(false)} title="Add New Department" onSave={handleAddDepartment} loading={deptCreating} />
      <QuickAddModal open={posModalOpen} onClose={() => setPosModalOpen(false)} title="Add New Position" onSave={handleAddPosition} loading={posCreating} />
    </Box>
  );
};

export default Index;
